import { supabase } from "@/services";
import dayjs from "@/lib/locale-dayjs";

// Define the function to get categorized appointments
export async function getInstallerAppointments(installerId: string) {
    const now = dayjs();
    const startOfDay = now.startOf("day"); // Start of today
    const endOfDay = now.endOf("day"); // End of today

    // Upcoming Appointments
    const { data: scheduled, error: scheduledError } = await supabase
        .from("appointments")
        .select(
            "*, projects (name, users (first_name, last_name, phone_number))",
        )
        .eq("status", "Scheduled")
        .eq("installer_id", installerId);

    if (scheduledError) {
        throw new Error(
            scheduledError.message,
        );
    }

    // Today's Appointments
    const { data: today, error: todayError } = await supabase
        .from("appointments")
        .select(
            "*, projects (name, users (first_name, last_name, phone_number))",
        )
        .eq("installer_id", installerId)
        .eq("status", "Scheduled")
        .gte("appointment_time", startOfDay.toISOString()) // Start of today
        .lte("appointment_time", endOfDay.toISOString()); // End of today

    if (todayError) {
        throw new Error(
            todayError.message,
        );
    }

    // Previous Appointments
    const { data: completed, error: completedError } = await supabase
        .from("appointments")
        .select(
            "*, projects (name, users (first_name, last_name, phone_number))",
        )
        .eq("installer_id", installerId)
        .eq("status", "Completed"); // Earlier than now

    if (completedError) {
        throw new Error(
            completedError.message,
        );
    }

    return {
        scheduled,
        today,
        completed,
    };
}

export async function getInstallerAppointmentsFiltered(
    installerId: string,
    filter:
        | string
        | "All"
        | "Today"
        | "This Week"
        | "This Month"
        | "Tomorrow"
        | "Completed"
        | "Upcoming",
    status: string | "All" | "Scheduled" | "Completed",
) {
    let query = supabase
        .from("appointments")
        .select(
            "*, projects (name, users (first_name, last_name, phone_number))",
        )
        .eq("installer_id", installerId);
    const now = dayjs();
    const startOfDay = now.startOf("day"); // Start of today
    const endOfDay = now.endOf("day"); // End of today
    const startOfWeek = now.startOf("week"); // Start of this week
    const endOfWeek = now.endOf("week"); // End of this week

    switch (filter) {
        case "All":
            break;
        case "Today":
            query = query
                .or(`and(appointment_time.gte.${startOfDay.toISOString()},appointment_time.lte.${endOfDay.toISOString()}),and(end_date.gte.${startOfDay.toISOString()},end_date.lte.${endOfDay.toISOString()})`);
            break;
        case "This Week":
            query = query
                .or(`and(appointment_time.gte.${startOfWeek.toISOString()},appointment_time.lte.${endOfWeek.toISOString()}),and(end_date.gte.${startOfWeek.toISOString()},end_date.lte.${endOfWeek.toISOString()})`);
            break;
        case "This Month":
            query = query
                .or(`and(appointment_time.gte.${
                    now.startOf("month").toISOString()
                },appointment_time.lte.${
                    now.endOf("month").toISOString()
                }),and(end_date.gte.${
                    now.startOf("month").toISOString()
                },end_date.lte.${now.endOf("month").toISOString()})`);
            break;
        case "Tomorrow":
            const startOfTomorrow = now.add(1, "day").startOf("day")
                .toISOString();
            const endOfTomorrow = now.add(1, "day").endOf("day").toISOString();
            query = query
                .or(`and(appointment_time.gte.${startOfTomorrow},appointment_time.lte.${endOfTomorrow}),and(end_date.gte.${startOfTomorrow},end_date.lte.${endOfTomorrow})`);
            break;
        case "Completed":
            query = query.eq("status", "Completed");
            break;
        default:
            break;
    }

    if (status !== "All") {
        query = query.eq("status", status);
    }

    query.order("appointment_time", { ascending: false });
    const { data, error } = await query;

    if (error) {
        throw new Error(
            error.message,
        );
    }
    return data;
}
